import { useEffect } from 'react';
import {
  checkWebPSupport,
  checkAVIFSupport,
  optimizeSupabaseImageUrl,
  generateResponsiveSrcset,
  shouldLazyLoad,
  getQualityByConnection
} from '@/utils/imageOptimizationUtils';

/**
 * Gestionnaire centralisé d'optimisation d'images
 * - Détection et application du format optimal (WebP/AVIF)
 * - Lazy loading intelligent basé sur la connexion
 * - Srcset responsive pour les images Supabase
 * - Audit des images non optimisées
 */
export function ImageOptimizationManager() {
  useEffect(() => {
    let webpSupported = false;
    let avifSupported = false;
    let connectionQuality = 80;

    // 1. Détecter le support des formats modernes
    const detectFormatSupport = async () => {
      webpSupported = await checkWebPSupport();
      avifSupported = await checkAVIFSupport();
      connectionQuality = getQualityByConnection();

      console.info('Image optimization initialized:', {
        webp: webpSupported,
        avif: avifSupported,
        quality: connectionQuality
      });
    };

    // 2. Optimiser une image existante
    const optimizeImage = (img: HTMLImageElement) => {
      const src = img.getAttribute('src');
      if (!src || !src.includes('supabase.co')) return;

      // Déterminer le format optimal
      const format = avifSupported ? 'avif' : webpSupported ? 'webp' : 'origin';

      // Optimiser l'URL avec le format et la qualité appropriés
      const optimizedSrc = optimizeSupabaseImageUrl(src, {
        quality: connectionQuality,
        format: format as any
      });

      // Générer un srcset responsive si l'image est suffisamment grande
      if (img.naturalWidth > 640) {
        const srcset = generateResponsiveSrcset(src);
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
        }
      }

      // Appliquer le lazy loading si l'image est en dehors du viewport
      if (!img.loading && shouldLazyLoad(img)) {
        img.loading = 'lazy';
      }

      // Mettre à jour la source si différente
      if (optimizedSrc !== src) {
        img.src = optimizedSrc;
      }

      // Ajouter decoding async pour ne pas bloquer le main thread
      if (!img.decoding) {
        img.decoding = 'async';
      }
    };

    // 3. Scanner et optimiser toutes les images Supabase
    const optimizeAllImages = () => {
      const images = document.querySelectorAll('img[src*="supabase.co"]');
      images.forEach((img) => optimizeImage(img as HTMLImageElement));
    };

    // 4. Observer les nouvelles images ajoutées au DOM
    const observeNewImages = () => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeName === 'IMG') {
              const img = node as HTMLImageElement;
              if (img.src?.includes('supabase.co')) {
                optimizeImage(img);
              }
            } else if (node instanceof Element) {
              const images = node.querySelectorAll('img[src*="supabase.co"]');
              images.forEach((img) => optimizeImage(img as HTMLImageElement));
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => observer.disconnect();
    };

    // 5. Auditer les images non optimisées (dev only)
    const auditImages = () => {
      if (process.env.NODE_ENV !== 'development') return;

      const allImages = document.querySelectorAll('img');
      const issues: string[] = [];

      allImages.forEach((img, index) => {
        const htmlImg = img as HTMLImageElement;

        // Vérifier la présence de width/height
        if (!htmlImg.width || !htmlImg.height) {
          issues.push(`Image ${index + 1}: Missing width/height (CLS risk)`);
        }

        // Vérifier le lazy loading
        if (!htmlImg.loading && shouldLazyLoad(htmlImg)) {
          issues.push(`Image ${index + 1}: Should use lazy loading`);
        }

        // Vérifier les images Supabase non optimisées
        if (htmlImg.src?.includes('supabase.co') && !htmlImg.srcset) {
          issues.push(`Image ${index + 1}: Supabase image without srcset`);
        }

        // Vérifier decoding
        if (!htmlImg.decoding) {
          issues.push(`Image ${index + 1}: Missing decoding="async"`);
        }
      });

      if (issues.length > 0) {
        console.warn('Image optimization issues found:', issues);
      }
    };

    // Initialiser l'optimisation
    const initialize = async () => {
      await detectFormatSupport();
      optimizeAllImages();
      const cleanup = observeNewImages();

      // Audit après un court délai pour laisser les images se charger
      setTimeout(auditImages, 2000);

      return cleanup;
    };

    let cleanup: (() => void) | undefined;
    initialize().then((fn) => (cleanup = fn));

    return () => {
      cleanup?.();
    };
  }, []);

  return null; // Composant sans UI
}
